import React, { useMemo, useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import serviceApi from '../services/serviceApi';
import SubscriptionPricingPanel from './services/SubscriptionPricingPanel';
import { CUSTOM_PRICING_UI_ENABLED } from './services/cleaningPricingConfig';

/**
 * CreateSubscriptionModal — Stage 6 catalog-flow refactor.
 *
 * Flow:
 *   1. Pick location → derives pricingTier
 *   2. Pricing panel handles package selection, discount, dates, custom path,
 *      and live quote preview (shared with the wizard's Step 3 — Stage 6c).
 *   3. Submit → server creates subscription + auto-issues invoice
 *
 * Custom path (no packageCode) reveals legacy fields for ad-hoc pricing
 * agreements (decision #32 — packageCode null is allowed).
 */
const CreateSubscriptionModal = ({ show, onHide, clientId, locations, onCreated }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    locationId: '',
    packageCode: '',
    discountAmount: '',
    billingCycleStart: '',
    priceNotes: '',
    listPrice: '',
    visitsIncluded: '',
    validityDays: '',
    billingCycleEnd: ''
  });

  const selectedLocation = useMemo(
    () => (locations || []).find((l) => l._id === form.locationId) || null,
    [locations, form.locationId]
  );
  const isCustomPath = !form.packageCode;

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleReset = () => {
    setForm({
      locationId: '', packageCode: '', discountAmount: '', billingCycleStart: '', priceNotes: '',
      listPrice: '', visitsIncluded: '', validityDays: '', billingCycleEnd: ''
    });
    setError(null);
  };

  const handleSubmit = async () => {
    setError(null);
    if (!form.locationId || !form.billingCycleStart) {
      setError('الموقع وتاريخ بداية الدورة مطلوبان');
      return;
    }
    // Catalog-only mode: require packageCode (custom path hidden — see
    // cleaningPricingConfig.js). When flag is on, fall back to legacy gate.
    if (!CUSTOM_PRICING_UI_ENABLED && !form.packageCode) {
      setError('يرجى اختيار باقة');
      return;
    }
    if (CUSTOM_PRICING_UI_ENABLED && isCustomPath) {
      if (!form.listPrice || !form.visitsIncluded) {
        setError('السعر القياسي وعدد الزيارات مطلوبان للسعر المخصص');
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        clientId,
        locationId: form.locationId,
        billingCycleStart: form.billingCycleStart,
        priceNotes: form.priceNotes || undefined,
        discountAmount: form.discountAmount ? parseFloat(form.discountAmount) : undefined
      };
      if (form.packageCode) {
        payload.packageCode = form.packageCode;
      } else {
        payload.listPrice = parseFloat(form.listPrice);
        payload.visitsIncluded = parseInt(form.visitsIncluded, 10);
        if (form.validityDays) payload.validityDays = parseInt(form.validityDays, 10);
        if (form.billingCycleEnd) payload.billingCycleEnd = form.billingCycleEnd;
      }
      await serviceApi.createSubscription(payload);
      handleReset();
      onCreated();
      onHide();
    } catch (err) {
      setError(err.response?.data?.error || 'حدث خطأ — يرجى المحاولة مرة أخرى');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered dir="rtl">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 700, color: '#471396' }}>
          إنشاء اشتراك جديد
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}

        <Form.Group className="mb-3">
          <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>الموقع *</Form.Label>
          <Form.Select
            className="touch-input"
            value={form.locationId}
            onChange={(e) => {
              handleChange('locationId', e.target.value);
              handleChange('packageCode', ''); // tier may differ — reset
            }}
          >
            <option value="">اختر الموقع...</option>
            {(locations || []).filter((l) => l.status === 'active').map((loc) => (
              <option key={loc._id} value={loc._id}>
                {loc.name} ({loc.locationCode})
                {loc.pricingTier ? ` — ${loc.pricingTier}` : ''}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <SubscriptionPricingPanel
          locationTier={selectedLocation?.pricingTier}
          locationId={form.locationId}
          values={form}
          onChange={handleChange}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>إلغاء</Button>
        <Button
          style={{ background: '#471396', borderColor: '#471396' }}
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? <span className="spinner-border spinner-border-sm" /> : 'إنشاء الاشتراك'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateSubscriptionModal;
